import classNames from "classnames";
import { X } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

import { type SkillSummary } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";
import { RobotoButton, Select } from "@/shared/foundation/primitives";

import { type AgentFormValues } from "../agentFormSchema";

import { LATEST_VERSION, skillVersionOptions } from "./agentSkills";
import styles from "./skills.module.css";

export interface SkillRowProps {
  index: number;
  /** The org-shared skill this row names, or absent when it no longer resolves. */
  summary: SkillSummary | undefined;
  /** The attached id, which is all a row has to show when `summary` is absent. */
  skillId: string;
  onRemove: () => void;
}

/**
 * One attached skill: what it is, what it does, and which version of it the
 * agent runs — an exact one, or whatever is newest when it launches.
 */
export function SkillRow({ index, summary, skillId, onRemove }: SkillRowProps) {
  const { control, getFieldState, formState } =
    useFormContext<AgentFormValues>();
  // Nothing to pin on a skill that no longer resolves, and its versions
  // endpoint would 404.
  const versionsQuery = useSkillVersions(
    summary === undefined ? undefined : skillId,
  );
  const rowError = getFieldState(`skills.${index}.skill_id`, formState).error;

  return (
    <div
      className={classNames(styles.row, {
        [styles.unavailable]: summary === undefined,
      })}
      data-testid={`agent-skill-${index}`}
      data-resolved={summary !== undefined}
    >
      <div className={styles.main}>
        <span className={styles.name}>{summary?.skill.name ?? skillId}</span>
        {summary?.latest_version !== null &&
          summary?.latest_version !== undefined && (
            <span className={styles.description}>
              {summary.latest_version.description}
            </span>
          )}
        {rowError?.message !== undefined && (
          <span className={styles.warning} role="alert">
            {rowError.message}
          </span>
        )}
      </div>

      {summary !== undefined && (
        <Controller
          control={control}
          name={`skills.${index}.version`}
          render={({ field }) => (
            <Select<string>
              className={styles.version}
              items={skillVersionOptions(
                typeof field.value === "number" ? field.value : null,
                summary.latest_version?.version ?? null,
                (versionsQuery.data ?? []).map((entry) => entry.version),
              )}
              value={
                typeof field.value === "number"
                  ? String(field.value)
                  : LATEST_VERSION
              }
              onValueChange={(chosen) => {
                field.onChange(
                  chosen === null || chosen === LATEST_VERSION
                    ? null
                    : Number(chosen),
                );
              }}
              aria-label={`Version of ${summary.skill.name}`}
              size="sm"
            />
          )}
        />
      )}

      <RobotoButton
        variant="ghost"
        icon={<X aria-hidden />}
        aria-label={`Remove ${summary?.skill.name ?? skillId}`}
        onClick={onRemove}
        data-testid={`agent-skill-${index}-remove`}
      />
    </div>
  );
}
